import React, { useEffect, useState } from "react";
import { Row, Col, Button, Alert, Accordion } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import PerformanceMetricSelector from "./PerformanceMetricSelector";
import PerformanceWeightageEditor from "./PerformanceWeightageEditor";

// Phase 5, §7/§8/§23/§32. Full profile editor: target fields (role/
// department/project/module/user override), metric attachment + weightage,
// validation preview, and resolution preview — never lets the browser
// compute or submit an arbitrary score formula, only structured fields.
export default function PerformanceProfileEditor({ profileId, onSaved }) {
  const [profile, setProfile] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [validation, setValidation] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!profileId) return;
    try {
      const [profileRes, metricsRes] = await Promise.all([
        api.get(`/performance-profiles/${profileId}`),
        api.get(`/performance-profiles/${profileId}/metrics`),
      ]);
      setProfile(profileRes.data?.data || null);
      setMetrics(metricsRes.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load profile");
    }
  };

  useEffect(() => { load(); }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addMetric = (metric) => {
    setMetrics((prev) => [...prev, {
      performance_metric_definition_id: metric.id, code: metric.code, name: metric.name, direction: metric.direction,
      target_value: metric.target_value, weightage: 0, include_in_score: metric.direction !== "INFORMATIONAL_ONLY",
      mandatory_data: false, target_value_override: null, is_active: 1,
    }]);
  };

  const removeMetric = (index) => setMetrics((prev) => prev.filter((_, i) => i !== index));

  const saveMetrics = async () => {
    setSaving(true);
    try {
      await api.put(`/performance-profiles/${profileId}/metrics`, { metrics });
      toast.success("Profile metrics saved");
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save profile metrics");
    } finally {
      setSaving(false);
    }
  };

  const runValidate = async () => {
    try {
      const res = await api.post(`/performance-profiles/${profileId}/validate`);
      setValidation(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Validation failed");
    }
  };

  if (!profile) return <div className="text-muted small">Select a profile to edit its metrics.</div>;

  return (
    <div>
      <Row className="g-2 mb-3">
        <Col md={3}><strong>Code:</strong> {profile.code}</Col>
        <Col md={3}><strong>Role ID:</strong> {profile.role_id || "Any"}</Col>
        <Col md={3}><strong>Department ID:</strong> {profile.department_id || "Any"}</Col>
        <Col md={3}><strong>Project ID:</strong> {profile.project_id || "Any"}</Col>
      </Row>

      <PerformanceWeightageEditor rows={metrics} onChange={setMetrics} onRemove={removeMetric} />

      <Accordion className="my-3">
        <Accordion.Item eventKey="0">
          <Accordion.Header>+ Add Metric</Accordion.Header>
          <Accordion.Body>
            <PerformanceMetricSelector excludeIds={metrics.map((m) => m.performance_metric_definition_id)} onAdd={addMetric} />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <div className="d-flex gap-2 mb-3">
        <Button variant="outline-secondary" onClick={runValidate}>Validate</Button>
        <Button variant="primary" onClick={saveMetrics} disabled={saving}>{saving ? "Saving..." : "Save Metrics"}</Button>
      </div>

      {validation && (
        <Alert variant={validation.valid ? "success" : "danger"}>
          {validation.valid ? "Profile is valid and ready to activate." : (
            <ul className="mb-0">{validation.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          )}
        </Alert>
      )}
    </div>
  );
}
